import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { Subscription, Observable } from 'rxjs'
import { finalize, switchMap } from 'rxjs/operators'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { UiService } from 'src/app/services/ui.service'
import { ApiService } from 'src/app/services/api.service'
import { handleArpioError } from '../../../../utils/handle-error'
import { AccessLevelService, AccountPermission } from 'src/app/services/access-level.service'

@Component({
  selector: 'app-account-resource-configs',
  templateUrl: './account-resource-configs.component.html',
  styleUrls: ['./account-resource-configs.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountResourceConfigsComponent implements OnInit, OnDestroy {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    private uiService: UiService,
    public accountService: AccountService,
    private accessLevelService: AccessLevelService,
    private router: Router
  ) {}

  private loadSubscription: Subscription = null
  private updateSubscription: Subscription = null
  private permissionsSubscription: Subscription = null
  private submitted = false

  // Permission properties
  private canManageResourceDefaultsPermission = false
  private permissionsLoaded = false

  get rbacEnabled(): boolean {
    return this.accountService.rbacEnabled
  }

  get canManageResourceDefaults(): boolean {
    return this.canManageResourceDefaultsPermission
  }

  resourceConfigsFormModel = this.formBuilder.group({
    s3RecoveryBucketSuffix: [
      '',
      [Validators.minLength(2), Validators.maxLength(12), Validators.pattern('^[a-z0-9]+$')],
    ],
  })

  get loading() {
    return this.loadSubscription != null
  }

  get updateButtonDisabled() {
    return !(this.resourceConfigsFormModel.dirty && this.resourceConfigsFormModel.valid)
  }

  ngOnInit() {
    // Initialize permissions
    this.loadPermissions()

    // Set up permission-based form control state based on cached permissions
    this.updateFormControlState()

    // load the subscription settings
    this.loadSubscription = this.accountService.accountIdStream
      .pipe(
        switchMap((_accountId) =>
          this.accountService.resourceTypeConfigs$.pipe(finalize(() => (this.loadSubscription = null)))
        )
      )
      .subscribe(
        (dto) => {
          this.resourceConfigsFormModel.setValue({
            s3RecoveryBucketSuffix: dto.configs['s3Bucket']?.recoveryBucketSuffix || '',
          })
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  ngOnDestroy() {
    if (this.loadSubscription) {
      this.loadSubscription.unsubscribe()
      this.loadSubscription = null
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe()
      this.updateSubscription = null
    }
    if (this.permissionsSubscription) {
      this.permissionsSubscription.unsubscribe()
      this.permissionsSubscription = null
    }
  }

  private loadPermissions() {
    // Resource defaults require UPDATE_ACCOUNT permission
    this.permissionsSubscription = this.accessLevelService
      .hasAccountPermission(AccountPermission.UPDATE_ACCOUNT)
      .subscribe((canManage) => {
        this.canManageResourceDefaultsPermission = canManage
        this.permissionsLoaded = true
        this.updateFormControlState()
      })
  }

  private updateFormControlState() {
    if (this.canManageResourceDefaults) {
      this.resourceConfigsFormModel.enable()
    } else {
      this.resourceConfigsFormModel.disable()
    }
  }

  updateResourceConfigs() {
    this.updateSubscription = this.accountService
      .setResourceTypeConfigs$({
        configs: {
          s3Bucket: {
            resourceType: 's3Bucket',
            recoveryBucketSuffix: this.resourceConfigsFormModel.value.s3RecoveryBucketSuffix,
          },
        },
      })
      .pipe(finalize(() => (this.updateSubscription = null)))
      .subscribe(
        () => {
          this.submitted = true
          this.resourceConfigsFormModel.markAsPristine()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  get submitting() {
    return subscriptionActive(this.updateSubscription)
  }

  get doneSubmitting() {
    return this.submitted && !this.resourceConfigsFormModel.dirty
  }
}
